/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import { Fragment, ReactNode } from 'react';

// eslint-disable-next-line @atlaskit/ui-styling-standard/use-compiled -- Ignored via go/DSP-18766
import { jsx } from '@emotion/react';

import { type StatusPanelProps } from '@af/design-system-docs-ui';
import { Box, xcss } from '@atlaskit/primitives';
import { Show } from '@atlaskit/primitives/responsive';

import BackToTop from '../__DO_NOT_ADD_TO_THIS_FOLDER__/gatsby-theme-brisk/components/back-to-top';
import FeedbackSection from '../__DO_NOT_ADD_TO_THIS_FOLDER__/gatsby-theme-brisk/components/feedback-analytics/feedback-section';
import Hero from '../__DO_NOT_ADD_TO_THIS_FOLDER__/gatsby-theme-brisk/components/hero';
import LocalNav from '../__DO_NOT_ADD_TO_THIS_FOLDER__/gatsby-theme-brisk/components/local-nav/local-nav';
import MetaTags from '../__DO_NOT_ADD_TO_THIS_FOLDER__/gatsby-theme-brisk/components/meta-tags';
import { mainContentStyles } from '../__DO_NOT_ADD_TO_THIS_FOLDER__/gatsby-theme-brisk/styles';
import { PageContentContainer } from '../components/page-content-container';
import useMounted from '../hooks/is-mounted';

interface PageProps {
	title: string;
	description?: string;
	status?: StatusPanelProps;
	children: any;
	tabName?: string;
	guidelineCategory?: string;
	parentPageTitle?: string;
	subpageTabName?: string;
	shouldHideLocalNav?: boolean;
}

/**
 * __Page__
 */
const Page = ({
	subpageTabName,
	title,
	description,
	status,
	children,
	tabName,
	guidelineCategory,
	parentPageTitle,
	shouldHideLocalNav,
}: PageProps) => {
	const checkMounted = useMounted();
	const pageUrl = checkMounted ? window.location.href : '';

	return (
		<Fragment>
			<MetaTags
				title={title}
				description={description}
				tabName={tabName}
				guidelineCategory={guidelineCategory}
				parentPageTitle={parentPageTitle}
				subpageTabName={subpageTabName}
			/>
			<Hero headline={title} description={description ? description : ''} status={status} />
			<div
				// eslint-disable-next-line @atlaskit/design-system/consistent-css-prop-usage, @atlaskit/ui-styling-standard/no-imported-style-values
				css={mainContentStyles}
				//eslint-disable-next-line @atlaskit/ui-styling-standard/no-classname-prop -- Ignored via go/DSP-18766
				className="main-content"
			>
				<PageContentContainer>
					{shouldHideLocalNav ? (
						<div>{children}</div>
					) : (
						<Grid>
							<div>{children}</div>
							<Show above="sm">
								<LocalNav />
							</Show>
						</Grid>
					)}
					<FeedbackSection
						subpageTabName={subpageTabName}
						tabName={tabName}
						title={title}
						pageUrl={pageUrl}
					/>
				</PageContentContainer>
			</div>
			<BackToTop label="Back to top" />
		</Fragment>
	);
};

const gridStyles = xcss({
	display: 'grid',
	gap: 'space.500',
	width: '100%',
	gridTemplateColumns: '1fr',
	// Needs to match the `Show above="sm"` breakpoint
	'@media (min-width: 48rem)': {
		gridTemplateColumns: '10fr 2fr',
	},
});

const Grid = ({ children }: { children: ReactNode }) => {
	return <Box xcss={gridStyles}>{children}</Box>;
};

export default Page;
